import { useId, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { Upload } from "lucide-react";
import { useAgents } from "@/components/Agents/AgentsContext";
import { apiPost } from "@/lib/apiClient";
import { withKbMembership } from "@/lib/knowledgeBaseRegistry";
import { agentsHref } from "@/lib/atwAgentsHost";
import {
  isMarkdownFilename,
  rejectNonMarkdownMessage,
  titleFromMarkdownFilename,
} from "@/lib/markdownArticleUpload";
import { articlePath, type KnowledgeArticle } from "@/lib/knowledgeModel";
import { knowledgeSecondaryLinkClass } from "@/pages/knowledge/KnowledgeChrome";
import { useKnowledgeBases } from "@/pages/knowledge/useKnowledgeBases";

/**
 * Beta: read a local .md in the browser, POST /api/knowledge/articles, then open VIEW.
 * Active knowledge base membership is applied; books stay optional.
 */
export function UploadMarkdownArticleButton() {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { orgContextStatus, orgContextError } = useAgents();
  const { activeId, hasBases } = useKnowledgeBases();
  const [uploading, setUploading] = useState(false);
  const blocked = orgContextStatus !== "ready" || !hasBases || !activeId.trim() || uploading;

  async function onFileChange(file: File | null) {
    if (!file) return;
    if (orgContextStatus !== "ready") {
      toast.error(orgContextError || "Organization context required.");
      if (inputRef.current) inputRef.current.value = "";
      return;
    }
    if (!hasBases || !activeId.trim()) {
      toast.error("Select or create a knowledge base first.");
      if (inputRef.current) inputRef.current.value = "";
      return;
    }
    if (!isMarkdownFilename(file.name)) {
      toast.error(rejectNonMarkdownMessage());
      if (inputRef.current) inputRef.current.value = "";
      return;
    }

    setUploading(true);
    try {
      const markdown = await file.text();
      const title = titleFromMarkdownFilename(file.name);
      const res = await apiPost<KnowledgeArticle>("/api/knowledge/articles", {
        title,
        markdown,
        status: "draft",
        collectionKeys: withKbMembership([], activeId),
      });
      if (!res.ok || !res.data?.id) {
        toast.error(res.error || "Could not create article from the uploaded file.");
        return;
      }
      toast.success("Article created from markdown.");
      void navigate(agentsHref(articlePath(activeId, res.data.id)), { replace: true });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not read the selected file.");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  return (
    <>
      <label
        htmlFor={inputId}
        className={`${knowledgeSecondaryLinkClass} cursor-pointer ${
          blocked ? "pointer-events-none opacity-50" : ""
        }`}
      >
        <Upload className="h-4 w-4" aria-hidden />
        {uploading ? "Uploading…" : "Upload markdown"}
      </label>
      <input
        id={inputId}
        ref={inputRef}
        type="file"
        accept=".md,text/markdown"
        className="sr-only"
        disabled={blocked}
        onChange={(event) => void onFileChange(event.target.files?.[0] ?? null)}
      />
    </>
  );
}
